import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  input,
  linkedSignal,
  resource,
  signal,
  viewChild
} from '@angular/core';
import { debouncedSignal, QueryFilter, uuid } from '@softline/core';
import { ButtonComponent, TranslatePipe } from '@softline/ui-core';
import { SelectDsItem, SelectDsMultiComponent } from '@softline/ui-core/select';
import { PageFilterItem } from '../../types/page-filter-item';
import { PageFilterService } from '../../services/page-filter.service';
import { Filter } from '../../../types/filter';
import { FilterItemConfig } from '../../../types/filter-config';
import { filterValueKey } from '../../../helper/filter-value-equals.function';
import { excludeFilterNode } from '../../../helper/exclude-filter-node.function';
import { toQueryFilter } from '../../../helper/to-query-filter.function';
import { PAGE_FILTER_VALUES_PAGE_SIZE, pagedFilterValues } from '../../helper/paged-filter-values.function';

export const PAGE_FILTER_SELECT_PAGE_SIZE = PAGE_FILTER_VALUES_PAGE_SIZE;

interface ItemsRequest {
  item: FilterItemConfig<any>;
  filter: QueryFilter<any> | null;
  page: number;
  search: string;
}

/**
 * A pinned in/notIn item as design-system multi select: the values come paged and searched
 * from the item's PageFilterDataService, the selection is a draft while the panel is open and
 * is committed by Übernehmen in the footer (closing any other way discards it).
 *
 * The select compares values by reference, so it works on stable string keys
 * (filterValueKey) and maps them back to the loaded/committed entities on apply.
 */
@Component({
  selector: 'soft-page-filter-select-item',
  imports: [SelectDsMultiComponent, ButtonComponent, TranslatePipe],
  templateUrl: './page-filter-select-item.component.html',
  styleUrl: './page-filter-select-item.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PageFilterSelectItemComponent {
  readonly item = input.required<PageFilterItem<any, any>>();
  readonly service = input.required<PageFilterService>();

  private readonly select = viewChild(SelectDsMultiComponent);

  readonly title = computed(() => `${this.item().config.title()}`);
  readonly committed = computed(() => this.service().getSourceItemValue(this.item()));
  readonly committedValues = computed<unknown[]>(() => {
    const value = (this.committed() as { value?: unknown } | null)?.value;
    return Array.isArray(value) ? value : [];
  });
  readonly committedKeys = computed(() => this.committedValues().map((v) => this.keyOf(v)));

  /** true while the panel is open - only then values are loaded and the draft may deviate */
  readonly editing = signal(false);
  readonly draftKeys = linkedSignal<string[], string[]>({
    source: this.committedKeys,
    computation: (keys) => keys,
  });

  readonly search = signal('');
  private readonly debouncedSearch = debouncedSignal(this.search, 300);
  readonly page = signal(0);

  private readonly queryFilter = computed(() => {
    const state = this.item().source.state;
    const value = this.committed();
    const tree = value ? excludeFilterNode(state.state(), value.uuid) : state.state();
    return tree ? toQueryFilter(tree, state.config) : null;
  });

  private readonly filterKey = computed(() => JSON.stringify(this.queryFilter()));

  // memoized so an equal-but-new-reference filter does not re-fire the loader
  private readonly request = computed<ItemsRequest | undefined>(
    () =>
      this.editing()
        ? { item: this.item().config, filter: this.queryFilter(), page: this.page(), search: this.debouncedSearch() }
        : undefined,
    {
      equal: (a, b) =>
        a === b ||
        (!!a && !!b && a.item === b.item && a.page === b.page && a.search === b.search &&
          JSON.stringify(a.filter) === JSON.stringify(b.filter)),
    }
  );

  // the paging, the failure handling and the empty-window walk are shared with the sidebar picker
  private readonly paged = pagedFilterValues<unknown, ItemsRequest>({
    request: this.request,
    page: this.page,
    load: (request, start, end) =>
      this.item().source.dataService.loadItems(request.item, request.filter, start, end, request.search),
  });
  /** loaded pages, accumulated while scrolling; page 0 replaces, a failed page keeps what we had */
  readonly loaded = this.paged.values;
  readonly hasMore = this.paged.hasMore;
  readonly loading = this.paged.loading;
  readonly loadFailed = this.paged.failed;
  readonly loadErrorDetail = this.paged.errorDetail;

  retry(): void {
    this.paged.retry();
  }

  /** committed entities first while there is no search term - the selection stays representable
      without padding a backend search result with non-matches - then the loaded page(s) */
  private readonly entities = computed(() => {
    const map = new Map<string, unknown>();
    const committed = this.debouncedSearch() ? [] : this.committedValues();
    for (const value of [...committed, ...this.loaded()]) map.set(this.keyOf(value), value);
    return map;
  });

  readonly items = computed<SelectDsItem<string>[]>(() => {
    const format = this.item().config.formatter as (value: unknown) => string;
    return Array.from(this.entities(), ([key, value]) => ({ value: key, label: format(value) }));
  });

  constructor() {
    // a new search or a changed filter starts paging over; the filter is only built while open
    effect(() => {
      if (!this.editing()) return;
      this.debouncedSearch();
      this.filterKey();
      this.page.set(0);
    });
  }

  onOpened(): void {
    this.page.set(0);
    this.draftKeys.set(this.committedKeys());
    this.editing.set(true);
  }

  /** closed without Übernehmen: the draft is dropped */
  onClosed(): void {
    this.editing.set(false);
    this.draftKeys.set(this.committedKeys());
    this.search.set('');
  }

  loadMore(): void {
    if (this.hasMore() && !this.loading()) this.page.update((page) => page + 1);
  }

  clear(): void {
    this.draftKeys.set([]);
  }

  apply(): void {
    const item = this.item();
    const entities = this.entities();
    const values = this.draftKeys().map((key) => entities.get(key)).filter((v) => v !== undefined);
    const current = this.committed();
    const filter =
      values.length === 0
        ? null
        : current
          ? ({ ...current, value: values } as Filter<any>)
          : ({ uuid: uuid(), type: item.config.type, field: item.field, operator: item.operator, value: values } as Filter<any>);
    this.service().applyItemFilter(item, filter, 'source');
    this.close();
  }

  close(): void {
    this.select()?.close();
  }

  private keyOf(value: unknown): string {
    return filterValueKey(this.item().config, value);
  }
}
